import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PaymentButton } from '@/components/payment/PaymentButton';

interface ClinicBookingDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  coachId: string;
  clubId: string;
  playerId: string;
  onSuccess?: () => void;
}

export const ClinicBookingDialog: React.FC<ClinicBookingDialogProps> = ({
  isOpen,
  onOpenChange,
  coachId,
  clubId,
  playerId,
  onSuccess
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    clinic_title: '',
    clinic_description: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    price_cents: 8000 // $80.00
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleBooking = async () => {
    setIsLoading(true);

    try {
      // Get current user (parent)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .rpc('get_effective_profile_id');

      // Create clinic booking
      const { data: booking, error } = await supabase
        .from('clinic_bookings')
        .insert({
          coach_id: coachId,
          club_id: clubId,
          player_id: playerId,
          parent_id: profile,
          clinic_title: formData.clinic_title,
          clinic_description: formData.clinic_description,
          scheduled_date: formData.scheduled_date,
          scheduled_time: formData.scheduled_time,
          duration_minutes: formData.duration_minutes,
          price_cents: formData.price_cents
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Clinic booking created successfully'
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: 'Error',
        description: 'Failed to create clinic booking',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Book Private Clinic</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Clinic Title</Label>
            <Input
              id="title"
              value={formData.clinic_title}
              onChange={(e) => 
                setFormData(prev => ({ ...prev, clinic_title: e.target.value }))
              }
              placeholder="Private Skills Training"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.clinic_description}
              onChange={(e) => 
                setFormData(prev => ({ ...prev, clinic_description: e.target.value }))
              }
              placeholder="Focus areas for the training session..."
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => 
                  setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))
                }
              />
            </div>
            <div>
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => 
                  setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => 
                  setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))
                }
              />
            </div>
            <div>
              <Label htmlFor="price">Price ($)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price_cents / 100}
                onChange={(e) => 
                  setFormData(prev => ({ ...prev, price_cents: Math.round(parseFloat(e.target.value) * 100) }))
                }
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            
            <div className="space-x-2">
              <Button onClick={handleBooking} disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Booking'}
              </Button>
              
              <PaymentButton
                amount={formData.price_cents / 100}
                paymentType="coach_clinic"
                recipientId={coachId}
                clubId={clubId}
                metadata={{ 
                  clinic_title: formData.clinic_title,
                  player_id: playerId 
                }}
              >
                Book & Pay Now
              </PaymentButton>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};